import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { EmptyState, ErrorState } from '../components/ui/feedback-state'
import ManagedCustomerAutocomplete from '../components/ManagedCustomerAutocomplete'
import { api, getApiErrorMessage } from '../lib/api'
import { formatKoreanPhoneInput } from '../lib/crmInputFormat'

function unwrap(res) {
  const body = res?.data
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

function toYmd(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function addMonths(d, months) {
  return new Date(d.getFullYear(), d.getMonth() + months, 1)
}

function monthStart(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

function monthEnd(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0)
}

const PERIOD_ALL_KEY = 'all'

function buildMaturityPeriods(baseDate = new Date()) {
  const today = baseDate
  const monthPeriod = (offset, key) => {
    const anchor = addMonths(today, offset)
    return {
      key,
      label: `${anchor.getMonth() + 1}월`,
      caption: `${anchor.getMonth() + 1}월`,
      from: toYmd(monthStart(anchor)),
      to: toYmd(monthEnd(anchor)),
    }
  }
  return [
    { key: PERIOD_ALL_KEY, label: '전체', caption: '전체', from: null, to: null },
    { key: 'dueToday', label: '오늘', caption: '오늘', from: toYmd(today), to: toYmd(today) },
    monthPeriod(0, 'dueInCurrentMonth'),
    monthPeriod(1, 'dueInNextMonth'),
    monthPeriod(2, 'dueInMonthPlus2'),
  ]
}

function normalizePhoneDigits(value) {
  const digits = String(value || '').replace(/\D/g, '')
  return digits.length === 10 || digits.length === 11 ? digits : ''
}

function daysUntil(ymd) {
  if (!ymd) return null
  const today = new Date()
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const target = new Date(`${ymd}T00:00:00`)
  if (Number.isNaN(target.getTime())) return null
  return Math.round((target.getTime() - start.getTime()) / 86400000)
}

function formatPhone(v) {
  const digits = String(v || '').replace(/\D/g, '')
  return digits ? formatKoreanPhoneInput(digits) : '-'
}

function formatRemainingDays(days) {
  if (days == null) return '-'
  if (days < 0) return `${Math.abs(days)}일 지남`
  if (days === 0) return '오늘'
  return `${days}일 남음`
}

function contractLabel(c) {
  if (!c) return '계약 연결 안 함'
  const parts = [c.insurerName, c.productName, c.policyNumberMasked, c.coveragePeriod].filter(Boolean)
  return parts.length ? parts.join(' · ') : `계약 #${c.id}`
}

const editableFields = [
  'policyLine',
  'nextMaturityDate',
  'insurerName',
  'productLabel',
  'enrollmentChannel',
  'vehicleNumber',
  'driverRestriction',
]

function toEditableDraft(row = {}) {
  return {
    customerRefId: row.customerRefId ? String(row.customerRefId) : '',
    customerName: row.customerName || '',
    customerPhone: row.customerPhone || '',
    policyLine: row.policyLine || 'AUTO',
    nextMaturityDate: row.nextMaturityDate || '',
    insurerName: row.insurerName || '',
    productLabel: row.productLabel || row.memo || '',
    enrollmentChannel: row.enrollmentChannel || '',
    vehicleNumber: row.vehicleNumber || '',
    driverRestriction: row.driverRestriction || '',
    linkedContractId: row.linkedContractId ? String(row.linkedContractId) : '',
  }
}

function emptyNewDraft() {
  return {
    tempId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    ...toEditableDraft(),
  }
}

function draftPayload(draft) {
  return {
    policyLine: draft.policyLine || 'AUTO',
    nextMaturityDate: draft.nextMaturityDate,
    insurerName: String(draft.insurerName || '').trim() || null,
    productLabel: String(draft.productLabel || '').trim() || null,
    enrollmentChannel: String(draft.enrollmentChannel || '').trim() || null,
    vehicleNumber: draft.policyLine === 'AUTO' ? String(draft.vehicleNumber || '').trim() || null : null,
    driverRestriction: draft.policyLine === 'AUTO' ? String(draft.driverRestriction || '').trim() || null : null,
    memo: null,
    linkedContractId: draft.linkedContractId ? Number(draft.linkedContractId) : 0,
  }
}

function isChangedDraft(row, draft) {
  const original = toEditableDraft(row)
  return editableFields.some((field) => String(original[field] || '') !== String(draft[field] || ''))
}

const CSV_COLUMNS = [
  { key: 'id', label: 'id' },
  { key: 'customerRefId', label: 'customerRefId' },
  { key: 'customerName', label: 'customerName' },
  { key: 'customerPhone', label: 'customerPhone' },
  { key: 'policyLine', label: 'policyLine' },
  { key: 'nextMaturityDate', label: 'nextMaturityDate' },
  { key: 'insurerName', label: 'insurerName' },
  { key: 'productLabel', label: 'productLabel' },
  { key: 'enrollmentChannel', label: 'enrollmentChannel' },
  { key: 'vehicleNumber', label: 'vehicleNumber' },
  { key: 'driverRestriction', label: 'driverRestriction' },
  { key: 'linkedContractId', label: 'linkedContractId' },
]

const CSV_HEADER_ALIASES = {
  '행ID': 'id',
  고객ID: 'customerRefId',
  고객명: 'customerName',
  연락처: 'customerPhone',
  구분: 'policyLine',
  만기일: 'nextMaturityDate',
  보험사: 'insurerName',
  메모: 'productLabel',
  가입경로: 'enrollmentChannel',
  차량번호: 'vehicleNumber',
  운전자한정: 'driverRestriction',
  연결계약ID: 'linkedContractId',
}

function csvEscape(value) {
  const s = value == null ? '' : String(value)
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

function csvCellValue(row, key) {
  if (key === 'customerPhone') return formatPhone(row.customerPhone)
  return row?.[key] ?? ''
}

function parseCsvLine(line) {
  const out = []
  let cur = ''
  let quoted = false
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i]
    const next = line[i + 1]
    if (quoted && ch === '"' && next === '"') {
      cur += '"'
      i += 1
    } else if (ch === '"') {
      quoted = !quoted
    } else if (ch === ',' && !quoted) {
      out.push(cur)
      cur = ''
    } else {
      cur += ch
    }
  }
  out.push(cur)
  return out
}

function parseCsv(text) {
  const normalized = String(text || '').replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const lines = normalized.split('\n').filter((line) => line.trim() !== '')
  if (lines.length === 0) return []
  const headers = parseCsvLine(lines[0]).map((h) => CSV_HEADER_ALIASES[String(h).trim()] || String(h).trim())
  return lines.slice(1).map((line) => {
    const cells = parseCsvLine(line)
    const row = {}
    headers.forEach((key, idx) => {
      row[key] = cells[idx] == null ? '' : cells[idx].trim()
    })
    return row
  })
}

function duplicateKey(draft) {
  return [
    draft.customerRefId,
    draft.policyLine || 'AUTO',
    draft.nextMaturityDate,
    draft.insurerName,
    draft.vehicleNumber,
  ].map((v) => String(v || '').trim().toLowerCase()).join('|')
}

const maturityLines = [
  { key: 'AUTO', label: '자동차보험', shortLabel: '자동차' },
  { key: 'GENERAL', label: '일반보험', shortLabel: '일반' },
]

const sheetInputClass = 'w-full min-w-0 rounded border border-slate-300 bg-white px-2 py-1.5 text-xs'
const sheetSelectClass = 'w-full min-w-0 rounded border border-slate-300 bg-white px-2 py-1.5 text-xs'

function dashboardLineValue(dashboard, line, periodKey) {
  const nested = dashboard?.byPolicyLine?.[line]?.[periodKey]
  if (typeof nested === 'number') return nested
  return 0
}

function dashboardTotalValue(dashboard, periodKey) {
  const direct = dashboard?.[periodKey]
  if (typeof direct === 'number') return direct
  return maturityLines.reduce((sum, line) => sum + dashboardLineValue(dashboard, line.key, periodKey), 0)
}

function buildListParams({ dateRange, policyLine, page, size }) {
  const params = { policyLine, page, size }
  if (dateRange.from && dateRange.to) {
    params.from = dateRange.from
    params.to = dateRange.to
  }
  return params
}

export default function CustomerMaturityLedgerPage() {
  const csvInputRef = useRef(null)
  const [rows, setRows] = useState([])
  const [pageInfo, setPageInfo] = useState({ page: 0, size: 25, totalElements: 0, totalPages: 0 })
  const [loading, setLoading] = useState(true)
  const [dashboard, setDashboard] = useState(null)
  const [dashboardLoading, setDashboardLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [csvBusy, setCsvBusy] = useState(false)
  const [err, setErr] = useState('')
  const [rowErrors, setRowErrors] = useState({})
  const [draftRows, setDraftRows] = useState({})
  const [newRows, setNewRows] = useState([])
  const [periodKey, setPeriodKey] = useState(PERIOD_ALL_KEY)
  const [policyLine, setPolicyLine] = useState('ALL')

  const maturityPeriods = useMemo(() => buildMaturityPeriods(), [])
  const visiblePeriod = maturityPeriods.find((p) => p.key === periodKey) || maturityPeriods[0]

  const dateRange = useMemo(() => ({
    from: visiblePeriod.from,
    to: visiblePeriod.to,
  }), [visiblePeriod.from, visiblePeriod.to])
  const periodDescription = visiblePeriod.key === PERIOD_ALL_KEY ? '전체 기간' : `${dateRange.from} ~ ${dateRange.to}`
  const periodFileKey = visiblePeriod.key === PERIOD_ALL_KEY ? 'all' : `${dateRange.from}-${dateRange.to}`

  const loadRows = useCallback(async (nextPage = pageInfo.page) => {
    setLoading(true)
    setErr('')
    try {
      const res = await api.get('/customer-management/maturity-policies', {
        params: buildListParams({
          dateRange,
          policyLine,
          page: nextPage,
          size: pageInfo.size,
        }),
      })
      const data = unwrap(res) || {}
      setRows(Array.isArray(data.items) ? data.items : [])
      setDraftRows({})
      setRowErrors({})
      setPageInfo({
        page: typeof data.page === 'number' ? data.page : nextPage,
        size: typeof data.size === 'number' ? data.size : pageInfo.size,
        totalElements: typeof data.totalElements === 'number' ? data.totalElements : 0,
        totalPages: typeof data.totalPages === 'number' ? data.totalPages : 0,
      })
    } catch (e) {
      const msg = getApiErrorMessage(e, '보험만기대장을 불러오지 못했습니다.')
      setErr(msg)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [dateRange, pageInfo.page, pageInfo.size, policyLine])

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true)
    try {
      const res = await api.get('/customer-management/maturity-policies/dashboard', { __silent: true })
      const data = unwrap(res) || {}
      setDashboard(data && typeof data === 'object' ? data : null)
    } catch {
      setDashboard(null)
    } finally {
      setDashboardLoading(false)
    }
  }, [])

  const loadAllRowsForCurrentFilter = useCallback(async () => {
    const all = []
    let page = 0
    let totalPages = 1
    while (page < totalPages) {
      const res = await api.get('/customer-management/maturity-policies', {
        params: buildListParams({
          dateRange,
          policyLine,
          page,
          size: 100,
        }),
        __silent: true,
      })
      const data = unwrap(res) || {}
      const items = Array.isArray(data.items) ? data.items : []
      all.push(...items)
      totalPages = typeof data.totalPages === 'number' ? data.totalPages : page + 1
      if (items.length === 0) break
      page += 1
    }
    return all
  }, [dateRange, policyLine])

  useEffect(() => {
    setPageInfo((prev) => ({ ...prev, page: 0 }))
    loadRows(0)
    loadDashboard()
  }, [dateRange.from, dateRange.to, policyLine, loadDashboard]) // eslint-disable-line react-hooks/exhaustive-deps

  const rowDraft = (row) => draftRows[row.id] || toEditableDraft(row)

  const setDraftField = (row, field, value) => {
    setDraftRows((prev) => {
      const base = prev[row.id] || toEditableDraft(row)
      const next = { ...base, [field]: value }
      if (field === 'policyLine' && value !== 'AUTO') {
        next.vehicleNumber = ''
        next.driverRestriction = ''
      }
      if (!isChangedDraft(row, next)) {
        const copy = { ...prev }
        delete copy[row.id]
        return copy
      }
      return { ...prev, [row.id]: next }
    })
  }

  const setNewField = (tempId, field, value) => {
    setNewRows((prev) => prev.map((row) => {
      if (row.tempId !== tempId) return row
      const next = { ...row, [field]: value }
      if (field === 'policyLine' && value !== 'AUTO') {
        next.vehicleNumber = ''
        next.driverRestriction = ''
      }
      if ((field === 'customerName' || field === 'customerPhone') && row.customerRefId) {
        next.customerRefId = ''
      }
      return next
    }))
  }

  const applyManagedCustomerToNewRow = (tempId, customer) => {
    if (!customer) return
    setNewRows((prev) => prev.map((row) => {
      if (row.tempId !== tempId) return row
      const phoneDigits = String(customer.customerPhone || '').replace(/\D/g, '')
      return {
        ...row,
        customerRefId: String(customer.id || ''),
        customerName: customer.customerName || row.customerName,
        customerPhone: phoneDigits ? formatKoreanPhoneInput(phoneDigits) : row.customerPhone,
      }
    }))
  }

  const addNewRow = () => {
    setNewRows((prev) => [...prev, emptyNewDraft()])
  }

  const removeNewRow = (tempId) => {
    setNewRows((prev) => prev.filter((row) => row.tempId !== tempId))
    setRowErrors((prev) => {
      const copy = { ...prev }
      delete copy[`new-${tempId}`]
      return copy
    })
  }

  const discardDrafts = () => {
    setDraftRows({})
    setNewRows([])
    setRowErrors({})
  }

  const validateDraft = (draft, needsCustomer) => {
    if (needsCustomer) {
      if (!draft.customerRefId) {
        const name = String(draft.customerName || '').trim()
        const phone = normalizePhoneDigits(draft.customerPhone)
        if (!name) return '이름을 입력해 주세요.'
        if (!phone) return '연락처는 숫자 10~11자리로 입력해 주세요.'
      }
    }
    if (!draft.nextMaturityDate) return '다음 만기일을 입력해 주세요.'
    return ''
  }

  const ensureCustomerRefId = async (draft) => {
    if (draft.customerRefId) return String(draft.customerRefId)
    const res = await api.post('/customer-management/customers/ensure-for-ledger', {
      customerName: String(draft.customerName || '').trim(),
      customerPhone: normalizePhoneDigits(draft.customerPhone),
    }, { __silent: true })
    const data = unwrap(res) || {}
    if (!data.id) throw new Error('고객 등록에 실패했습니다.')
    return String(data.id)
  }

  const hasNewRowContent = (row) => (
    row.customerRefId
    || row.customerName
    || row.customerPhone
    || row.nextMaturityDate
    || row.insurerName
    || row.productLabel
    || row.enrollmentChannel
    || row.vehicleNumber
    || row.driverRestriction
  )

  const saveSheet = async () => {
    const changedRows = rows
      .filter((row) => draftRows[row.id] && isChangedDraft(row, draftRows[row.id]))
      .map((row) => ({ row, draft: draftRows[row.id] }))
    const insertRows = newRows.filter(hasNewRowContent)

    if (changedRows.length === 0 && insertRows.length === 0) {
      try { window?.toast?.show?.('저장할 변경사항이 없습니다.', 'info') } catch { }
      return
    }

    const nextErrors = {}
    changedRows.forEach(({ row, draft }) => {
      const message = validateDraft(draft, false)
      if (message) nextErrors[`row-${row.id}`] = message
    })
    insertRows.forEach((draft) => {
      const message = validateDraft(draft, true)
      if (message) nextErrors[`new-${draft.tempId}`] = message
    })
    if (Object.keys(nextErrors).length > 0) {
      setRowErrors(nextErrors)
      try { window?.toast?.show?.('저장 전 확인이 필요한 행이 있습니다.', 'error') } catch { }
      return
    }

    setSaving(true)
    setErr('')
    setRowErrors({})
    try {
      for (const { row, draft } of changedRows) {
        await api.patch(
          `/customer-management/customers/${row.customerRefId}/maturity-policies/${row.id}`,
          draftPayload(draft),
          { __silent: true },
        )
      }
      for (const draft of insertRows) {
        const customerRefId = await ensureCustomerRefId(draft)
        await api.post(
          `/customer-management/customers/${customerRefId}/maturity-policies`,
          draftPayload(draft),
          { __silent: true },
        )
      }
      try { window?.toast?.show?.('만기대장 변경사항을 저장했습니다.', 'success') } catch { }
      setNewRows([])
      await loadRows(insertRows.length > 0 ? 0 : pageInfo.page)
      await loadDashboard()
    } catch (e) {
      const msg = getApiErrorMessage(e, '만기대장 변경사항 저장에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setSaving(false)
    }
  }

  const downloadCsv = async () => {
    setCsvBusy(true)
    setErr('')
    try {
      const allRows = await loadAllRowsForCurrentFilter()
      const header = CSV_COLUMNS.map((col) => col.label).join(',')
      const body = allRows.map((row) => CSV_COLUMNS.map((col) => csvEscape(csvCellValue(row, col.key))).join(',')).join('\n')
      const csv = `\uFEFF${header}\n${body}`
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `maturity-ledger-${periodFileKey}-${policyLine}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      const msg = getApiErrorMessage(e, 'CSV 다운로드에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setCsvBusy(false)
    }
  }

  const uploadCsv = async (file) => {
    if (!file) return
    setCsvBusy(true)
    setErr('')
    try {
      const csvRows = parseCsv(await file.text())
      const imports = csvRows
        .map((row) => ({
          id: String(row.id || '').trim(),
          draft: {
            customerRefId: String(row.customerRefId || '').trim(),
            customerName: String(row.customerName || '').trim(),
            customerPhone: String(row.customerPhone || '').trim(),
            policyLine: String(row.policyLine || 'AUTO').trim().toUpperCase() === 'GENERAL' ? 'GENERAL' : 'AUTO',
            nextMaturityDate: String(row.nextMaturityDate || '').trim(),
            insurerName: String(row.insurerName || '').trim(),
            productLabel: String(row.productLabel || '').trim(),
            enrollmentChannel: String(row.enrollmentChannel || '').trim(),
            vehicleNumber: String(row.vehicleNumber || '').trim(),
            driverRestriction: String(row.driverRestriction || '').trim(),
            linkedContractId: String(row.linkedContractId || '').trim(),
          },
        }))
        .filter(({ id, draft }) => (
          id
          || draft.customerRefId
          || draft.customerName
          || draft.customerPhone
          || draft.nextMaturityDate
          || draft.insurerName
          || draft.productLabel
          || draft.enrollmentChannel
          || draft.vehicleNumber
          || draft.driverRestriction
        ))

      if (imports.length === 0) {
        try { window?.toast?.show?.('업로드할 행이 없습니다.', 'error') } catch { }
        return
      }

      const allRows = await loadAllRowsForCurrentFilter()
      const byId = new Map(allRows.map((row) => [String(row.id), row]))
      const byKey = new Map()
      allRows.forEach((row) => {
        const key = duplicateKey(toEditableDraft(row))
        if (!byKey.has(key)) byKey.set(key, row)
      })

      const errors = []
      imports.forEach(({ draft }, idx) => {
        const message = validateDraft(draft, true)
        if (message) errors.push(`${idx + 2}행: ${message}`)
      })
      if (errors.length > 0) {
        const msg = errors.slice(0, 5).join('\n')
        setErr(msg)
        try { window?.toast?.show?.('CSV에서 확인이 필요한 행이 있습니다.', 'error') } catch { }
        return
      }

      let updated = 0
      let inserted = 0
      let customersCreated = 0
      const customerRefCache = new Map()
      for (const { draft } of imports) {
        if (draft.customerRefId) continue
        const phone = normalizePhoneDigits(draft.customerPhone)
        if (phone && customerRefCache.has(phone)) {
          draft.customerRefId = customerRefCache.get(phone)
          continue
        }
        const res = await api.post('/customer-management/customers/ensure-for-ledger', {
          customerName: String(draft.customerName || '').trim(),
          customerPhone: phone,
        }, { __silent: true })
        const data = unwrap(res) || {}
        if (!data.id) throw new Error('고객 등록에 실패했습니다.')
        draft.customerRefId = String(data.id)
        if (data.created) customersCreated += 1
        if (phone) customerRefCache.set(phone, draft.customerRefId)
      }
      for (const { id, draft } of imports) {
        const target = (id && byId.get(id)) || byKey.get(duplicateKey(draft))
        if (target) {
          await api.patch(
            `/customer-management/customers/${target.customerRefId}/maturity-policies/${target.id}`,
            draftPayload(draft),
            { __silent: true },
          )
          updated += 1
        } else {
          await api.post(
            `/customer-management/customers/${draft.customerRefId}/maturity-policies`,
            draftPayload(draft),
            { __silent: true },
          )
          inserted += 1
        }
      }
      const createdMsg = customersCreated > 0 ? `, 고객 신규등록 ${customersCreated}건` : ''
      try { window?.toast?.show?.(`CSV 반영 완료: 업데이트 ${updated}건, 신규 ${inserted}건${createdMsg}`, 'success') } catch { }
      setNewRows([])
      setDraftRows({})
      await loadRows(pageInfo.page)
      await loadDashboard()
    } catch (e) {
      const msg = getApiErrorMessage(e, 'CSV 업로드에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setCsvBusy(false)
    }
  }

  const closeRow = async (row) => {
    if (!row?.id || !row?.customerRefId) return
    if (!window.confirm('이 만기·갱신 행을 종료하고 자동 등록된 안내 일정을 취소할까요?')) return
    setSaving(true)
    setErr('')
    try {
      await api.delete(`/customer-management/customers/${row.customerRefId}/maturity-policies/${row.id}`, { __silent: true })
      await loadRows(pageInfo.page)
      await loadDashboard()
      try { window?.toast?.show?.('만기·갱신 행을 종료했습니다.', 'success') } catch { }
    } catch (e) {
      const msg = getApiErrorMessage(e, '종료 처리에 실패했습니다.')
      setErr(msg)
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setSaving(false)
    }
  }

  const canPrev = pageInfo.page > 0
  const canNext = pageInfo.totalPages > 0 && pageInfo.page + 1 < pageInfo.totalPages
  const changedCount = rows.filter((row) => draftRows[row.id] && isChangedDraft(row, draftRows[row.id])).length
  const newCount = newRows.filter(hasNewRowContent).length
  const hasSheetChanges = changedCount > 0 || newCount > 0

  return (
    <div className="py-2">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">보험만기대장</h1>
        </div>
      </div>

      {err ? (
        <div className="mb-4 rounded-2xl border border-rose-200 bg-white">
          <ErrorState description={err} onAction={() => loadRows(pageInfo.page)} className="py-6" />
        </div>
      ) : null}

      <section className="mb-4">
        <div className="mb-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-xs font-bold text-slate-900">만기관리 대시보드</div>
          </div>
          {dashboardLoading ? <span className="text-xs text-slate-400">요약 갱신 중...</span> : null}
        </div>
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <div className="min-w-[560px]">
            <div className="grid grid-cols-[88px_repeat(5,minmax(0,1fr))] bg-slate-50 text-[11px] font-bold text-slate-500">
              <div className="px-2 py-1.5">구분</div>
              {maturityPeriods.map((period) => (
                <div key={period.key} className="px-2 py-1.5 text-right">{period.caption}</div>
              ))}
            </div>
            {maturityLines.map((line) => (
              <div
                key={line.key}
                className={`grid grid-cols-[88px_repeat(5,minmax(0,1fr))] border-t border-slate-100 ${line.key === 'AUTO' ? 'bg-sky-50/40' : 'bg-emerald-50/40'
                  }`}
              >
                <div className="flex items-center px-2 py-2 text-xs font-black text-slate-900">{line.shortLabel}</div>
                {maturityPeriods.map((period) => {
                  const value = dashboardLineValue(dashboard, line.key, period.key)
                  const active = policyLine === line.key && periodKey === period.key
                  return (
                    <button
                      key={`${line.key}-${period.key}`}
                      type="button"
                      className={`min-h-[42px] border-l border-slate-100 px-2 py-1.5 text-right transition ${active
                        ? 'bg-white text-brand-navy ring-1 ring-inset ring-brand-sky/30'
                        : 'bg-transparent text-slate-900 hover:bg-white/70'
                        }`}
                      onClick={() => {
                        setPolicyLine(line.key)
                        setPeriodKey(period.key)
                      }}
                    >
                      <div className="text-lg font-black tabular-nums">{Number(value || 0).toLocaleString()}</div>
                    </button>
                  )
                })}
              </div>
            ))}
            <div className="grid grid-cols-[88px_repeat(5,minmax(0,1fr))] border-t border-slate-200 bg-slate-50">
              <div className="flex items-center px-2 py-1.5 text-xs font-black text-slate-700">전체</div>
              {maturityPeriods.map((period) => (
                <button
                  key={`total-${period.key}`}
                  type="button"
                  className={`border-l border-slate-200 px-2 py-1.5 text-right transition ${policyLine === 'ALL' && periodKey === period.key ? 'bg-sky-50 text-brand-navy ring-1 ring-inset ring-brand-sky/30' : 'hover:bg-white'
                    }`}
                  onClick={() => {
                    setPolicyLine('ALL')
                    setPeriodKey(period.key)
                  }}
                >
                  <div className="text-base font-black tabular-nums">{Number(dashboardTotalValue(dashboard, period.key) || 0).toLocaleString()}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-800">만기 계약 목록· 총 {pageInfo.totalElements.toLocaleString()}건</div>
            <div className="mt-0.5 text-xs font-medium text-slate-500">
              {periodDescription} · 총 {pageInfo.totalElements.toLocaleString()}건
              {hasSheetChanges ? ` · 변경 ${changedCount}건 · 추가 ${newCount}건` : ''}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={addNewRow}
            >
              + 새 행
            </button>
            <button
              type="button"
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              disabled={csvBusy}
              onClick={downloadCsv}
            >
              CSV 다운로드
            </button>
            <button
              type="button"
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              disabled={csvBusy}
              onClick={() => csvInputRef.current?.click()}
            >
              CSV 업로드
            </button>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                e.target.value = ''
                uploadCsv(file)
              }}
            />
            <button
              type="button"
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              disabled={!hasSheetChanges || saving || csvBusy}
              onClick={discardDrafts}
            >
              취소
            </button>
            <button
              type="button"
              className="rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-bold text-white hover:bg-teal-700 disabled:opacity-50"
              disabled={!hasSheetChanges || saving || csvBusy}
              onClick={saveSheet}
            >
              {saving ? '저장 중...' : '변경사항 저장'}
            </button>
            <select
              value={periodKey}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800"
              onChange={(e) => setPeriodKey(e.target.value)}
            >
              {maturityPeriods.map((period) => (
                <option key={period.key} value={period.key}>{period.caption}</option>
              ))}
            </select>
            <select
              value={policyLine}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800"
              onChange={(e) => setPolicyLine(e.target.value)}
            >
              <option value="ALL">전체</option>
              <option value="AUTO">자동차</option>
              <option value="GENERAL">일반</option>
            </select>
            <button
              type="button"
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => { loadRows(pageInfo.page); loadDashboard() }}
              disabled={loading}
            >
              새로고침
            </button>
          </div>
        </div>
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500">불러오는 중...</div>
        ) : rows.length === 0 && newRows.length === 0 ? (
          <EmptyState
            title="기간 내 만기·갱신 대상이 없습니다"
            className="py-10"
          />
        ) : (
          <div className="overflow-auto">
            <table className="w-full min-w-[1480px] table-fixed border-collapse text-left text-sm">
              <colgroup>
                <col className="w-[180px]" />
                <col className="w-[140px]" />
                <col className="w-[88px]" />
                <col className="w-[96px]" />
                <col className="w-[88px]" />
                <col className="w-[120px]" />
                <col className="w-[120px]" />
                <col className="w-[120px]" />
                <col className="w-[140px]" />
                <col className="w-[210px]" />
                <col className="w-[180px]" />
                <col className="w-[78px]" />
              </colgroup>
              <thead className="bg-slate-50 text-xs font-semibold text-slate-500">
                <tr>
                  <th className="px-2 py-3">이름</th>
                  <th className="px-2 py-3">만기</th>
                  <th className="whitespace-nowrap px-2 py-3">남은 일</th>
                  <th className="px-2 py-3">구분</th>
                  <th className="px-2 py-3">개인/법인</th>
                  <th className="px-2 py-3">가입경로</th>
                  <th className="px-2 py-3">차량번호</th>
                  <th className="px-2 py-3">운전자한정</th>
                  <th className="px-2 py-3">가입 보험사</th>
                  <th className="px-2 py-3">연결 계약</th>
                  <th className="px-2 py-3">메모</th>
                  <th className="px-2 py-3 text-center">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {newRows.map((draft) => {
                  const error = rowErrors[`new-${draft.tempId}`]
                  return (
                    <tr key={draft.tempId} className="bg-teal-50/60 align-top">
                      <td className="px-2 py-3">
                        <ManagedCustomerAutocomplete
                          value={draft.customerName}
                          onValueChange={(next) => setNewField(draft.tempId, 'customerName', next)}
                          selectedCustomerId={draft.customerRefId}
                          onSelectCustomer={(customer) => applyManagedCustomerToNewRow(draft.tempId, customer)}
                          onClearCustomer={() => setNewField(draft.tempId, 'customerRefId', '')}
                          inputClassName={`${sheetInputClass} ${error && !String(draft.customerName || '').trim() ? 'border-rose-300' : 'border-slate-300'}`}
                          placeholder="이름"
                        />
                        <input
                          value={draft.customerPhone}
                          className={`${sheetInputClass} mt-1 ${error && !normalizePhoneDigits(draft.customerPhone) ? 'border-rose-300' : 'border-slate-300'}`}
                          placeholder="연락처"
                          onChange={(e) => setNewField(draft.tempId, 'customerPhone', formatKoreanPhoneInput(e.target.value.replace(/\D/g, '')))}
                        />
                        {error ? <div className="mt-1 text-[11px] font-semibold text-rose-600">{error}</div> : null}
                      </td>
                      <td className="px-2 py-3">
                        <input
                          type="date"
                          value={draft.nextMaturityDate}
                          className={sheetInputClass}
                          onChange={(e) => setNewField(draft.tempId, 'nextMaturityDate', e.target.value)}
                        />
                      </td>
                      <td className="whitespace-nowrap px-2 py-3 text-xs text-slate-500">{formatRemainingDays(daysUntil(draft.nextMaturityDate))}</td>
                      <td className="px-2 py-3">
                        <select
                          value={draft.policyLine}
                          className={sheetSelectClass}
                          onChange={(e) => setNewField(draft.tempId, 'policyLine', e.target.value)}
                        >
                          <option value="AUTO">자동차</option>
                          <option value="GENERAL">일반</option>
                        </select>
                      </td>
                      <td className="px-2 py-3 text-xs text-slate-400">신규</td>
                      <td className="px-2 py-3">
                        <input value={draft.enrollmentChannel} className={sheetInputClass} onChange={(e) => setNewField(draft.tempId, 'enrollmentChannel', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <input value={draft.vehicleNumber} className={`${sheetInputClass} disabled:bg-slate-100`} disabled={draft.policyLine !== 'AUTO'} onChange={(e) => setNewField(draft.tempId, 'vehicleNumber', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <input value={draft.driverRestriction} className={`${sheetInputClass} disabled:bg-slate-100`} disabled={draft.policyLine !== 'AUTO'} onChange={(e) => setNewField(draft.tempId, 'driverRestriction', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <input value={draft.insurerName} className={sheetInputClass} onChange={(e) => setNewField(draft.tempId, 'insurerName', e.target.value)} />
                      </td>
                      <td className="px-2 py-3 text-xs text-slate-400">-</td>
                      <td className="px-2 py-3">
                        <input value={draft.productLabel} className={sheetInputClass} onChange={(e) => setNewField(draft.tempId, 'productLabel', e.target.value)} />
                      </td>
                      <td className="px-2 py-3 text-center">
                        <button
                          type="button"
                          className="rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          onClick={() => removeNewRow(draft.tempId)}
                        >
                          제거
                        </button>
                      </td>
                    </tr>
                  )
                })}
                {rows.map((row) => {
                  const draft = rowDraft(row)
                  const changed = Boolean(draftRows[row.id])
                  const draftDday = daysUntil(draft.nextMaturityDate)
                  const error = rowErrors[`row-${row.id}`]
                  return (
                    <tr
                      key={row.id}
                      className={`${row.policyLine === 'AUTO'
                        ? 'bg-sky-50/20 hover:bg-sky-50/50'
                        : row.policyLine === 'GENERAL'
                          ? 'bg-emerald-50/20 hover:bg-emerald-50/50'
                          : 'hover:bg-slate-50'
                        } ${changed ? 'ring-1 ring-inset ring-amber-300' : ''}`}
                    >
                      <td className="px-2 py-3">
                        <Link className="font-semibold text-brand-navy hover:underline" to={`/support/customers/${row.customerRefId}#maturity`}>
                          {row.customerName || '(이름 없음)'}
                        </Link>
                        <div className="mt-0.5 text-xs text-slate-500">{formatPhone(row.customerPhone)}</div>
                        {error ? <div className="mt-1 text-[11px] font-semibold text-rose-600">{error}</div> : null}
                        {changed ? <div className="mt-1 text-[11px] font-bold text-amber-700">수정됨</div> : null}
                      </td>
                      <td className="px-2 py-3">
                        <input
                          type="date"
                          value={draft.nextMaturityDate}
                          className={sheetInputClass}
                          onChange={(e) => setDraftField(row, 'nextMaturityDate', e.target.value)}
                        />
                      </td>
                      <td className="px-2 py-3">
                        <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-bold ${draftDday == null
                          ? 'bg-slate-100 text-slate-600'
                          : draftDday <= 7
                            ? 'bg-rose-50 text-rose-700'
                            : 'bg-amber-50 text-amber-800'
                          }`}>
                          {formatRemainingDays(draftDday)}
                        </span>
                      </td>
                      <td className="px-2 py-3">
                        <select
                          value={draft.policyLine}
                          className={sheetSelectClass}
                          onChange={(e) => setDraftField(row, 'policyLine', e.target.value)}
                        >
                          <option value="AUTO">자동차</option>
                          <option value="GENERAL">일반</option>
                        </select>
                      </td>
                      <td className="px-2 py-3">{row.customerSegment || '-'}</td>
                      <td className="px-2 py-3">
                        <input value={draft.enrollmentChannel} className={sheetInputClass} onChange={(e) => setDraftField(row, 'enrollmentChannel', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <input value={draft.vehicleNumber} className={`${sheetInputClass} disabled:bg-slate-100`} disabled={draft.policyLine !== 'AUTO'} onChange={(e) => setDraftField(row, 'vehicleNumber', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <input value={draft.driverRestriction} className={`${sheetInputClass} disabled:bg-slate-100`} disabled={draft.policyLine !== 'AUTO'} onChange={(e) => setDraftField(row, 'driverRestriction', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <input value={draft.insurerName} className={sheetInputClass} onChange={(e) => setDraftField(row, 'insurerName', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <div className="truncate text-xs text-slate-600" title={contractLabel(row.linkedContract)}>
                          {row.linkedContract ? contractLabel(row.linkedContract) : '-'}
                        </div>
                      </td>
                      <td className="px-2 py-3">
                        <input value={draft.productLabel} className={sheetInputClass} onChange={(e) => setDraftField(row, 'productLabel', e.target.value)} />
                      </td>
                      <td className="px-2 py-3">
                        <div className="flex justify-center gap-1.5">
                          <button
                            type="button"
                            className="rounded border border-rose-200 bg-white px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                            disabled={saving}
                            onClick={() => closeRow(row)}
                          >
                            종료
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-col gap-2 border-t border-slate-100 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-slate-500">
            {pageInfo.totalPages > 0 ? `${pageInfo.page + 1} / ${pageInfo.totalPages} 페이지` : '0 / 0 페이지'}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              disabled={!canPrev || loading}
              onClick={() => loadRows(pageInfo.page - 1)}
            >
              이전
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              disabled={!canNext || loading}
              onClick={() => loadRows(pageInfo.page + 1)}
            >
              다음
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}
